/** The mobile cache's publish verdict, in words — whether the guest's app shows the
 *  booking and what a refresh would do NOW (plan §3: "shows the is_published verdict
 *  before refresh (a refresh can tombstone)"). Icon AND text: never colour alone. Shared
 *  by the Cache tab and the refresh confirmation, so both read the verdict the same way. */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import type { BookingVcacheHiddenReason, BookingVcachePublish } from '@/api/bookingActions';

const HIDDEN: Record<BookingVcacheHiddenReason, string> = {
  booking_state: 'its state is not one the app shows',
  tour_hidden: 'My Trips display is off on the tour',
  booking_hidden: 'My Trips display is off on the booking',
  staff_visibility_window: 'a staff booking outside its publish window',
  past_trip: 'the trip is long over',
  error: 'it could not be computed',
};

function Line({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Typography variant="body2" component="p" sx={{ m: 0, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
      <Box component="span" sx={{ display: 'inline-flex', mt: '2px', flex: 'none' }}>
        {icon}
      </Box>
      <span>{children}</span>
    </Typography>
  );
}

const ICON = { fontSize: 16 } as const;

/** "Shown in the guest's app" / "Not shown — <why>". */
export function PublishVerdict({ publish }: { publish: BookingVcachePublish }) {
  if (publish.is_published === null) {
    return (
      <Line icon={<ErrorOutlineIcon aria-hidden sx={{ ...ICON, color: 'text.secondary' }} />}>
        Whether the app shows it could not be computed.
      </Line>
    );
  }
  if (!publish.is_published) {
    return (
      <Line icon={<VisibilityOffOutlinedIcon aria-hidden sx={{ ...ICON, color: 'warning.main' }} />}>
        <strong>Not shown in the guest’s app</strong> — {HIDDEN[publish.reason ?? 'error']}.
      </Line>
    );
  }
  return (
    <Line icon={<CheckCircleOutlineIcon aria-hidden sx={{ ...ICON, color: 'success.main' }} />}>
      <strong>Shown in the guest’s app</strong>
      {publish.reason === 'past_trip' || publish.is_relevant === false ? ' — but the trip is long over.' : '.'}
    </Line>
  );
}

/** What a refresh does now, from the verdict. */
export function RefreshEffect({ publish }: { publish: BookingVcachePublish }) {
  switch (publish.refresh_effect) {
    case 'unpublish':
      return (
        <Line icon={<WarningAmberIcon aria-hidden sx={{ ...ICON, color: 'warning.main' }} />}>
          A refresh <strong>tombstones</strong> its tour_participant record: the app removes the booking.
        </Line>
      );
    case 'none':
      return (
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          A refresh changes nothing: the trip is long over.
        </Typography>
      );
    default:
      return (
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          A refresh rewrites its tour_participant record (and the legacy tour_package); the shared tour template and
          guest profile are left as they are.
        </Typography>
      );
  }
}
